import React, { useState, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Alert, CircularProgress, Box,
} from '@mui/material';

const PinRequestDialog = ({ open, onClose, kdnr, posConfig, onSuccess }) => {
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [sendungsnummer, setSendungsnummer] = useState('');
    const [spedition, setSpedition] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [smsWarning, setSmsWarning] = useState(null);

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWsEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleWsEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);
            if (msg.task === 'respRequestPinFromCustomer') {
                setSubmitting(false);
                if (msg.status === 'succeeded') {
                    setSuccess('PIN-Anforderung wurde versendet.');
                    setError(null);
                    if (msg.smsWarning) setSmsWarning(msg.smsWarning);
                    setTimeout(() => {
                        if (onSuccess) onSuccess();
                    }, 1500);
                } else {
                    setError(msg.message || 'Fehler beim Senden der Anforderung.');
                }
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, [onSuccess]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!kdnr) {
            setError('Kein Kunde ausgewählt.');
            return;
        }
        setError(null);
        setSuccess(null);
        setSmsWarning(null);
        setSubmitting(true);

        const payload = {
            task: 'requestPinFromCustomer',
            station: cookies.station,
            staff: cookies.staff,
            kdnr: Number(kdnr),
        };
        if (sendungsnummer.trim()) payload.sendungsnummer = sendungsnummer.trim();
        if (spedition.trim()) payload.spedition = spedition.trim();

        sendMessage(JSON.stringify(payload));
    };

    const handleClose = () => {
        setSendungsnummer('');
        setSpedition('');
        setError(null);
        setSuccess(null);
        setSmsWarning(null);
        setSubmitting(false);
        onClose();
    };

    const isConnected = readyState === ReadyState.OPEN;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
                PIN anfordern – Kunde {kdnr}
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}
                {smsWarning && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{smsWarning}</Alert>}

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Sendungsnummer"
                        value={sendungsnummer}
                        onChange={(e) => setSendungsnummer(e.target.value)}
                        size="small"
                        helperText="Optional"
                    />
                    <TextField
                        label="Spedition"
                        value={spedition}
                        onChange={(e) => setSpedition(e.target.value)}
                        size="small"
                        helperText="Optional"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} sx={{ textTransform: 'none', fontSize: 13 }}>
                    Abbrechen
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    disabled={submitting || !isConnected || !!success}
                    sx={{
                        borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 13,
                        bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' },
                    }}
                >
                    {submitting ? <CircularProgress size={20} /> : 'Anforderung senden'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PinRequestDialog;
